import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type { ButtonProps } from '@xing-com/button';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';

import * as Styled from './info-state.styles';

export type InternalButtonProps = ButtonProps & { copyKey?: string };

export type InfoStateProps = {
  icon?: React.ReactNode;
  headlineCopyKey?: string;
  headlineCopy?: string | React.ReactNode;
  headlineClass?: string;
  bodyClass?: string;
  textCopyKey: string;
  className?: string;
  testId?: string;
  additionalInfo?: React.ReactNode;
  buttonProps?: InternalButtonProps | Array<InternalButtonProps>;
  customButton?: React.ReactNode;
  statusCode?: 404;
};

export const InfoState: FC<InfoStateProps> = ({
  icon,
  headlineCopyKey,
  headlineCopy,
  headlineClass,
  bodyClass,
  textCopyKey,
  className,
  testId,
  buttonProps = [],
  additionalInfo,
  customButton,
  // statusCode,
}) => {
  const { $t } = useIntl();
  const isEditing = useEditStore(({ isEditing }) => isEditing);
  const hasCopyKey = headlineCopyKey && !headlineCopy;

  const buttonPropsList =
    buttonProps && Array.isArray(buttonProps) ? buttonProps : [buttonProps];

  return (
    <>
      {/* {statusCode && <Status code={Number(statusCode)} />} */}
      <Styled.InfoStateWrapper
        $isEditing={!!isEditing}
        className={className}
        data-testid={testId}
      >
        {icon && (
          <Styled.IconWrapper>
            {typeof icon === 'string' ? (
              <Styled.Icon src={icon} alt={'Icon'} />
            ) : (
              icon
            )}
          </Styled.IconWrapper>
        )}
        <Styled.Info>
          <div>
            <Styled.TextWrapper $noIcon={!icon}>
              <Styled.HeaderText
                fontWeight="bold"
                lineHeight="dense"
                size="xlarge"
                noMargin
                className={headlineClass}
              >
                {hasCopyKey && $t({ id: headlineCopyKey })}
                {!hasCopyKey && headlineCopy}
              </Styled.HeaderText>
              <Styled.Text noMargin size="small" className={bodyClass}>
                {$t({ id: textCopyKey, defaultMessage: textCopyKey })}
              </Styled.Text>
            </Styled.TextWrapper>
          </div>
          {additionalInfo && (
            <Styled.ExtraContent>{additionalInfo}</Styled.ExtraContent>
          )}
        </Styled.Info>
        {buttonPropsList.length > 0 && (
          <Styled.CtaContainer>
            {buttonPropsList.map(({ copyKey, ...props }) => (
              <Styled.Button
                size="small"
                sizeConfined="medium"
                variant={isEditing ? 'secondary' : 'tertiary'}
                {...props}
                className={props.className}
                key={copyKey}
              >
                {$t({
                  id: copyKey,
                  defaultMessage: copyKey,
                })}
              </Styled.Button>
            ))}
          </Styled.CtaContainer>
        )}
        {customButton !== null && customButton}
      </Styled.InfoStateWrapper>
    </>
  );
};
